import {
  partBox,
  resolvePreset,
  type AssemblyInstance,
  type AssemblyPanel,
  type CabinetInstance,
  type EvaluatedCabinet,
  type PanelOrientation,
  type PresetContent,
  type ProjectDocument,
  type Vec3,
} from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';

/** Immutable preset versions are cached forever per session (they never change). */
const versionCache = new Map<string, Promise<PresetContent>>();

export function loadVersion(versionId: string): Promise<PresetContent> {
  let p = versionCache.get(versionId);
  if (!p) {
    p = Api.presetVersion(versionId).then((v) => v.content);
    p.catch(() => versionCache.delete(versionId));
    versionCache.set(versionId, p);
  }
  return p;
}

export function newUnitId(doc: ProjectDocument, prefix: string): string {
  const used = new Set([...doc.cabinets.map((c) => c.id), ...doc.assemblies.map((a) => a.id)]);
  let n = doc.cabinets.length + doc.assemblies.length + 1;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

function nextX(doc: ProjectDocument, ev?: EvaluatedCabinet[]): number {
  if (!ev) return doc.cabinets.reduce((m, c) => Math.max(m, c.transform.position[0] + c.params.width), 0);
  return ev.reduce((m, c) => Math.max(m, c.instance.transform.position[0] + c.result.bounds.size[0]), 0);
}

/** Creates a new instance from a preset version. Fresh ids every time — nothing is shared with other instances. */
export function instantiatePreset(
  doc: ProjectDocument,
  preset: PresetSummaryDto,
  versionId: string,
  version: number,
  content: PresetContent,
  ev?: EvaluatedCabinet[],
): { kind: 'cabinet'; unit: CabinetInstance } | { kind: 'assembly'; unit: AssemblyInstance } {
  const x = nextX(doc, ev);
  const transform = { position: [x, 0, 0] as Vec3, rotationY: 0 as const, mirrored: false };
  if (content.kind === 'assembly') {
    const id = newUnitId(doc, 's');
    // A plinth ("dodaje se") or legs stand it on the floor: lifted by the room under the carcass.
    const plinth = content.features?.trims?.bottom;
    const under = Math.max(plinth?.size ?? 0, content.features?.legs?.height ?? 0) - (plinth?.mode === 'shrink' ? plinth.size : 0);
    if (under > 0) transform.position[1] = under;
    return {
      kind: 'assembly',
      unit: {
        id,
        name: `${id.toUpperCase()} ${preset.name}`,
        panels: structuredClone(content.panels),
        transform,
        materials: { ...content.materials },
        edgeOverrides: {},
        hidden: false,
        locked: false,
        environment: 'dry' as const,
        preset: { presetId: preset.id, versionId, version },
        // Shelves, fronts, drawers, plinth / fillers, legs and slope saved with the assembly.
        ...(content.features ? structuredClone(content.features) : {}),
      },
    };
  }
  const r = resolvePreset(content, {});
  if (!r.params) throw new Error(r.errors.join(' '));
  const id = newUnitId(doc, 'k');
  return {
    kind: 'cabinet',
    unit: {
      id,
      name: `${id.toUpperCase()} ${preset.name}`,
      params: r.params,
      transform,
      materials: { ...content.materials },
      edgeOverrides: {},
      hidden: false,
      locked: false,
      environment: 'dry' as const,
      preset: { presetId: preset.id, versionId, version, values: Object.fromEntries(content.params.map((p) => [p.key, p.default])) },
    },
  };
}

// ---------- selection → static assembly ----------

const AX = ['x', 'y', 'z'] as const;

/** Applies a unit's quarter-turn rotation and mirror to a local box; returns a world-space box. */
function toWorld(c: EvaluatedCabinet, min: Vec3, size: Vec3): { min: Vec3; size: Vec3 } {
  const w = c.result.bounds.size[0];
  const xs = c.instance.transform.mirrored ? [w - (min[0] + size[0]), w - min[0]] : [min[0], min[0] + size[0]];
  const corners: [number, number][] = [];
  for (const x of xs) for (const z of [min[2], min[2] + size[2]]) corners.push([x, z]);
  const phi = (-c.instance.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const [px, py, pz] = c.instance.transform.position;
  const wx = corners.map(([x, z]) => px + x * cos + z * sin);
  const wz = corners.map(([x, z]) => pz - x * sin + z * cos);
  const mn: Vec3 = [Math.min(...wx), py + min[1], Math.min(...wz)];
  return { min: mn, size: [Math.max(...wx) - mn[0], size[1], Math.max(...wz) - mn[2]] };
}

/** World axis that a local axis maps to after a quarter turn (mirror does not change axes). */
function worldAxis(local: 'x' | 'y' | 'z', rotationY: number): 'x' | 'y' | 'z' {
  if (local === 'y') return 'y';
  return rotationY % 180 === 0 ? local : local === 'x' ? 'z' : 'x';
}

/**
 * Converts the selected units into one static assembly: every part becomes a free panel positioned
 * relative to the selection's left/back/floor corner. No parametric relations are invented.
 */
export function selectionToAssembly(units: EvaluatedCabinet[]): { panels: AssemblyPanel[]; warnings: string[]; origin: Vec3 } {
  const warnings: string[] = [];
  const boxes: { c: EvaluatedCabinet; key: string; label: string; world: { min: Vec3; size: Vec3 }; part: EvaluatedCabinet['result']['parts'][number] }[] = [];
  for (const c of units) {
    if (Object.keys(c.instance.edgeOverrides).length) warnings.push(`${c.instance.name}: ručne izmjene kanta se ne prenose u sklop (ivice se ponovo određuju automatski).`);
    if (c.result.parts.some((p) => p.movable)) warnings.push(`${c.instance.name}: fronte i ladice postaju nepomične ploče (bez okova).`);
    for (const part of c.result.parts) {
      const b = partBox(part);
      boxes.push({ c, part, key: `${c.instance.id}-${part.localKey}`.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 60), label: `${c.instance.name}: ${part.label}`.slice(0, 200), world: toWorld(c, b.min, b.size) });
    }
  }
  const keyByPartId = new Map(boxes.map((b) => [b.part.id, b.key]));
  const origin: Vec3 = [0, 1, 2].map((k) => Math.min(...boxes.map((b) => b.world.min[k]!))) as Vec3;
  const panels: AssemblyPanel[] = boxes.map(({ c, part, key, label, world }) => {
    const thAxis = worldAxis(part.placement.thicknessAxis, c.instance.transform.rotationY);
    const orientation: PanelOrientation = thAxis === 'y' ? 'horizontal' : thAxis === 'x' ? 'vertical-side' : 'vertical-front';
    const lengthAxis = orientation === 'vertical-side' ? 'y' : 'x';
    const widthAxis = orientation === 'horizontal' ? 'z' : orientation === 'vertical-side' ? 'z' : 'y';
    const grainLocal = part.grain === 'none' ? null : part.grain === 'length' ? part.placement.lengthAxis : part.placement.widthAxis;
    const grainWorld = grainLocal ? worldAxis(grainLocal, c.instance.transform.rotationY) : null;
    const size = world.size;
    return {
      key,
      label,
      orientation,
      length: size[AX.indexOf(lengthAxis)]!,
      width: size[AX.indexOf(widthAxis)]!,
      thickness: size[AX.indexOf(thAxis)]!,
      position: [world.min[0] - origin[0], world.min[1] - origin[1], world.min[2] - origin[2]],
      materialSlot: part.materialSlot,
      grain: grainWorld === null ? 'none' : grainWorld === lengthAxis ? 'length' : 'width',
      repeat: null,
      joints: part.connections
        .filter((j) => j.kind === 'groove' || j.kind === 'rebate')
        .map((j) => ({ key: keyByPartId.get(j.partId)!, kind: j.kind as 'groove' | 'rebate' }))
        .filter((j) => j.key),
    };
  });
  return { panels, warnings: [...new Set(warnings)], origin };
}
